import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LanguageSwitcher from './components/LanguageSwitcher';
import { LanguageProvider } from './contexts/LanguageContext';
import { UserProvider, useUser } from './contexts/UserContext';
import { ErrorProvider } from './contexts/ErrorContext';
import ErrorBoundary from './components/ErrorBoundary';
import ErrorScreen from './components/ErrorScreen';
import ConnectionError from './components/ConnectionError';
import AuthError from './components/AuthError';
import NotFoundError from './components/NotFoundError';
import meetandeatLogo from './assets/meetandeat-logo.png';
import { checkSupabaseConnection, supabase, forceSessionRefresh } from './lib/supabase';
import MobileNavBar from './components/MobileNavBar';

import Home from './pages/Home';
import RestaurantSignup from './pages/RestaurantSignup';
import BlindDates from './pages/BlindDates';
import Nearby from './pages/Nearby';
import Reservations from './pages/Reservations';
import DrinkingBuddies from './pages/DrinkingBuddies';
import CreateListing from './pages/CreateListing';
import NearbyListings from './pages/NearbyListings';
import AuthCallback from './pages/AuthCallback';
import Register from './pages/Register';
import FeedbackPage from './pages/Feedback';
import SupabaseRedirectHandler from './pages/SupabaseRedirectHandler';
import Login from './pages/Login';
import UpdatePassword from './pages/UpdatePassword';
import MyVenues from './pages/MyVenues';
import MyEvents from './pages/MyEvents';
import MyDinners from './pages/MyDinners';
import MyReservations from './pages/MyReservations';
import CreateDinner from './pages/CreateDinner';
import CreateDrinkingNight from './pages/CreateDrinkingNight';
import CreateBlindDate from './pages/CreateBlindDate';
import Chat from './pages/Chat';
import ChatList from './pages/ChatList';
import FAQ from './pages/FAQ';
import LegalNotice from './pages/LegalNotice';
import OurPhilosophy from './pages/OurPhilosophy';
import OurTeam from './pages/OurTeam';
import RestaurantDetails from './pages/RestaurantDetails';
import FixRestaurantPictures from './pages/FixRestaurantPictures';
import UserProfile from './pages/UserProfile';
import ProfileSettings from './pages/ProfileSettings';
import Confirm from './pages/Confirm';
import ErrorPage from './pages/ErrorPage';
import Premium from './pages/Premium';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';

const allowedEmails = ['testhuber@gmail.com', 'ronaldm@test.at', 'elli@test.at', 'technogurke44@gmail.com', 'silviad@test.at', 'kathi@test.at', 'karina@test.at', 'allgemein@test.at', 'floa@test.at','basscharlie@test.at', 'trommelcharly@test.at', 'chrissys@test.at','stefank@test.at', 'alinad@test.at', 'karints@test.at', 'michaelts@test.at', 'eliasts@test.at', 'jessya@test.at', 'klaraa@test.at', 'kenanue@test.at', 'markuso@test.at', 'jaquiw@test.at','angieb@test.at'];

// Separate ScrollToTop component that uses useLocation
function ScrollToTopWrapper() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AppContent() {
  const { user, setUser, authChecked } = useUser();
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [connectionRetries, setConnectionRetries] = useState(0);
  const [loginError, setLoginError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if the current route is the confirm route
  const isConfirmRoute = location.pathname === "/confirm";
  const isAuthCallbackRoute = location.pathname === "/auth/callback";
  const isAuthVerifyRoute = location.pathname === "/auth/v1/verify";
  const isHomePage = location.pathname === "/";
  
  // Check if the URL has a confirmed=true parameter (user just confirmed email)
  const urlParams = new URLSearchParams(location.search);
  const isConfirmed = urlParams.get('confirmed') === 'true';

  useEffect(() => {
    const checkConnection = async () => {
      try {
        // Add timeout to connection check
        const connectionPromise = checkSupabaseConnection(2, 1500);
        const timeoutPromise = new Promise<boolean>((_, reject) => {
          setTimeout(() => reject(new Error('Connection timeout')), 10000);
        });

        let isConnected = false;
        try {
          isConnected = await Promise.race([connectionPromise, timeoutPromise]);
        } catch (err: any) {
          console.warn('Connection check failed:', err);
          if (err.message === 'Failed to fetch' || err.name === 'TypeError' || err.message.includes('timeout')) {
            if (connectionRetries < 3) {
              setTimeout(() => {
                setConnectionRetries(prev => prev + 1);
              }, Math.pow(2, connectionRetries) * 1000);
              return;
            } else {
              setConnectionError(true);
              return;
            }
          }
          throw err;
        }

        if (!isConnected && connectionRetries < 3) {
          // Retry with exponential backoff
          setTimeout(() => {
            setConnectionRetries(prev => prev + 1);
          }, Math.pow(2, connectionRetries) * 1000);
        } else {
          setConnectionError(!isConnected);
        }
      } catch (err: any) {
        console.error("Connection check failed:", err);
        if (connectionRetries < 3) {
          setTimeout(() => {
            setConnectionRetries(prev => prev + 1);
          }, Math.pow(2, connectionRetries) * 1000);
        } else {
          setConnectionError(true);
        }
      }
    };
    checkConnection();
  }, [connectionRetries]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      // Navigate to login page after sign out
      navigate('/login');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  // Connection Error with Retry Button
  if (connectionError) {
    return (
      <ConnectionError 
        onRetry={() => {
          setConnectionRetries(0);
          setConnectionError(false);
        }}
        message="Unable to connect to the server. Please check your internet connection and try again."
      />
    );
  }

  // Auth check loading state
  if (!authChecked) {
    return <div className="p-8 text-center">🔄 Authentifizierung wird geprüft...</div>;
  }

  // Update Password route
  const isPasswordUpdateRoute = window.location.pathname === '/update-password';
  if (!user && isPasswordUpdateRoute) {
    return (
      <LanguageProvider>
        <UpdatePassword />
      </LanguageProvider>
    );
  }

  // Access Denied
  if (user && !allowedEmails.includes(user.email)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-orange-50 text-center p-8">
        <h1 className="text-2xl font-bold text-orange-600 mb-2">⛔ Kein Zugriff</h1>
        <p className="mb-4">Deine E-Mail ist nicht für die Testphase freigeschaltet.</p>
        <button onClick={handleLogout} className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700 transition">Logout</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {!isHomePage && <Navbar />}
      <main className={`flex-grow ${!isHomePage ? 'pt-16' : ''} pb-16 md:pb-0`}>
        <ScrollToTopWrapper />
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/nearby" element={<Nearby />} />
            <Route path="/restaurant-signup" element={<RestaurantSignup />} />
            <Route path="/my-venues" element={<MyVenues />} />
            <Route path="/my-events" element={<MyEvents />} />
            <Route path="/my-dinners" element={<MyDinners />} />
            <Route path="/my-reservations" element={<MyReservations />} />
            <Route path="/create-dinner" element={<CreateDinner />} />
            <Route path="/create-drinking-night" element={<CreateDrinkingNight />} />
            <Route path="/create-blind-date" element={<CreateBlindDate />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/profile/:id" element={<UserProfile />} />
            <Route path="/profile-settings" element={<ProfileSettings />} />
            <Route path="/auth/v1/verify" element={<SupabaseRedirectHandler />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/blind-dates" element={<BlindDates />} />
            <Route path="/reservations" element={<Reservations />} />
            {/* DrinkingBuddies route removed but component kept */}
            <Route path="/update-password" element={<UpdatePassword />} />
            <Route path="/discover" element={<CreateListing />} />
            <Route path="/nearby-listings" element={<NearbyListings />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/confirm" element={<Confirm />} />
            <Route path="/chat" element={<ChatList />} />
            <Route path="/chat/:roomId" element={<Chat />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/legal" element={<LegalNotice />} />
            <Route path="/philosophy" element={<OurPhilosophy />} />
            <Route path="/team" element={<OurTeam />} />
            <Route path="/restaurant/:id" element={<RestaurantDetails />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/admin/fix-pictures" element={<FixRestaurantPictures />} />
            <Route path="/error/:code" element={<ErrorPage />} />
            <Route path="/premium" element={<Premium />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancelled" element={<PaymentCancelled />} />
            <Route path="*" element={<NotFoundError />} />
          </Routes>
        </ErrorBoundary>
      </main>
      <Footer />
      <LanguageSwitcher />
      <MobileNavBar />
    </div>
  );
}

export default function App() {
  return (
    <ErrorProvider>
      <Router>
        <LanguageProvider>
          <UserProvider>
            <ErrorBoundary>
              <AppContent />
            </ErrorBoundary>
          </UserProvider>
        </LanguageProvider>
      </Router>
    </ErrorProvider>
  );
}